import * as React from 'react';
import {
    useParams,
    useHistory
} from 'react-router-dom';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import * as classNames from 'classnames';
import { useSvcGraphs } from '../Svc/Graphing/UseSvcGraphs';
import { SvcDataPoint, SvcFlow, SvcTestFlow, OverreadChallengeStatusEnum, OverreadStatusEnum, OverreadStatusIdEnum, FixedCommentMutation, TestAcceptabilityMutation } from "../../misc/api/apiTypes";
import { SessionNote, StudyFixedCommentSet, SvcAnalysisSet, SvcTest, WebPortalClient } from 'webportalclient';
import { ChallengeAlert } from '../Shared/ChallengeAlert';
import { FixedCommentEntityTypeEnum } from '../../misc/api/FixedComments/FixedCommentSetEntityTypes';
import { useAcceptability, LoadingBestValuesAction, UpdateBestTestAction, UpdateRepeatabilityAction, DoneLoadingBestValuesAction } from '../Shared/UseAcceptability';
import { extractFirstCycle, extractExpiratoryCurveData, adjustTimeToTZero } from "./SvcSessionAnalyzer";
import { SvcGraphs } from './Graphing/SvcGraphs';
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { SvcSessionMetadataTable, SvcSessionMetadata } from './SvcSessionMetadataTable';
import { useSecurityContext } from '../../Core/SecurityContext';
import { SpirometryAnalysisSetTypeEnum } from '../../misc/api/ApiSpirometryAnalysisSetType';
import { SvcSession } from 'webportalclient';
import { SvcResultsTable } from './SvcResultsTable'
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import AcceptabilityDashboard from '../Shared/AcceptabilityDashboard';
import { SvcResults, StudySvcRelevantParameters } from '../Shared/PFTAPITypes';
import { useFixedComments, ToggleSessionFixedCommentsVisibilityAction } from '../../PulmonaryFunctionTests/Shared/UseFixedComments';
import { FixedCommentSetTypeEnum } from '../../misc/api/FixedComments/FixedCommentSetTypes';
import { SummaryPanel } from '../Shared/SummaryPanel';
import { ToColorHex } from '../Shared/TestColorMap';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { GoBackToList } from '../Shared/AnalysisHelpers';

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: "view" | "overread";
}

export interface SvcGraphTestData extends SvcTestFlow {
    ExpiratoryOnlyData: SvcDataPoint[];
    TZeroAdjustedData: SvcDataPoint[];
}

export const SvcAnalysisScreen = (): JSX.Element => {
    const history = useHistory();
    const { analysisSetId, viewState } = useParams<ExpectedMatchParams>();
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [adjustedToTZero, setAdjustedToTZero] = React.useState(false);
    const [volumeData, setVolumeData] = React.useState<SvcFlow>(null);
    const [analysisSet, setAnalysisSet] = React.useState<SvcAnalysisSet>(null);
    const [analysisSets, setAnalysisSets] = React.useState<SvcAnalysisSet[]>(null);
    const [currentSession, setSession] = React.useState<SvcSession>(null);
    const { currentUserPermissions } = useSecurityContext();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [sessionMetadata, setSessionMetadata] = React.useState<SvcSessionMetadata>(null);
    const [error, setError] = React.useState<string | undefined>();
    const client = useWebPortalApiClient();
    const [isAnalysisRejected, setIsAnalysisRejected] = React.useState<boolean>(false);
    const [SvcResults, SetSvcResults] = React.useState<SvcResults>(null);
    const [technicianTestCommentsAvailable, setTechnicianTestCommentsAvailable] = React.useState(false);
    const [technicianSessionCommentsAvailable, setTechnicianSessionCommentsAvailable] = React.useState(false);
    const [studySvcRelevantParameters, setStudySvcRelevantParameters] = React.useState<StudySvcRelevantParameters>(null);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [fixedCommentState, fixedCommentDispatch] = useFixedComments(SvcResults, SessionTypes.Svc);
    const legacyClient = useWebPortalLegacyClient();

    // Dispatch
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(SvcResults);

    // Dynamic styling
    const tZeroToggleClasses = classNames(
        'fas',
        'fa-fw',
        {
            'fa-toggle-on': adjustedToTZero,
            'fa-toggle-off': !adjustedToTZero
        }
    );

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    const addNewNote = async (annotation: string) => {
        await legacyClient.putSessionNotes(analysisSet.sessionId, annotation);
        await fetchSessionNotes(analysisSet.sessionId);
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);
        setIsAnalysisRejected(decision === OverreadStatusEnum.Rejected);

        const sessionFixedCommentsMutation = fixedCommentState.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentState.testFixedComments.map(x => ({
            TestId: x.id, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({
            TestId: x.TestId,
            Accepted: x.OverreaderAcceptability === null
                ? x.TechnicianAcceptability
                    ? true
                    : false
                : x.OverreaderAcceptability
        } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateAcceptabilitySvc(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            goBackToList();
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const SetAnalysisSets = async (sessionId: string) : Promise<SvcAnalysisSet[]> => {
        const analysisSets = await client.client.getSvcAnalysisSets('type', `sessionId eq ${sessionId}`);

        setAnalysisSets(analysisSets.value);

        return analysisSets.value;
    }

    const ConstructMetadata = async (currentSession: SvcSession, analysisSet: SvcAnalysisSet) => {
        const visit = currentSession.visit;
        const subject = visit.subject;
        const site = subject.site;
        const study = site.study;

        const metaData: SvcSessionMetadata = {
            analysisSet: analysisSet,
            session: currentSession,
            site: site,
            study: study,
            subject: subject,
            visit: visit,
        }

        setSessionMetadata(metaData);
    };

    const FetchSvcResultsData = async (): Promise<SvcResults> => {
        const test = await legacyClient.getSvcResultData(analysisSetId);
        return test;
    }

    const fetchSvcDataPoints = (svctest: SvcTest): Array<SvcDataPoint> => {
        const points: Array<SvcDataPoint> = [];

        const volumeSamplePoints = svctest.volumeData.split(',');
        const flowSamplePoints = svctest.flowData.split(',');

        let timePointCounter = 0;

        for (let i = 0; i < volumeSamplePoints.length; i++) {
            points.push({
                Volume: Number(volumeSamplePoints[i]),
                Time: ++timePointCounter / 100,
                Flow: Number(flowSamplePoints[i]),
            });
        }

        return points;
    };

    React.useEffect(() => {
        const initializeData = async () => {

            // fetch study specific parameters
            const parameters = await legacyClient.getStudySvcRelevantParameters();
            setStudySvcRelevantParameters(parameters);

            if (viewState === "overread") {
                setIsViewOnly(false);
                await legacyClient.startSvcOverread(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const analysisSet = await client.client.getSvcAnalysisSet(analysisSetId, undefined, 'session($select=id,name,technician,overreadChallengeStatus;$expand=visit($select=name,subject;$expand=subject($select=number;$expand=site($select=number,country,name,study,studyId;$expand=study($select=name,protocol)))))');
            const svcSession = await client.client.getSvcSession(analysisSet.sessionId, undefined, 'tests,notes');

            setSessionNotes(svcSession.notes);

            let flow: SvcFlow, tests: Array<SvcTestFlow> = [];

            const orderedTests: SvcTest[] = svcSession.tests.sort((a, b) => a.index - b.index);

            for (let i = 0; i < orderedTests.length; i++) {
                const currentTest = orderedTests[i];

                tests.push({
                    ColorHex: ToColorHex(currentTest.index),
                    IsDisplayed: true,
                    TestId: currentTest.id,
                    TestNumber: currentTest.index,
                    VolumeData: fetchSvcDataPoints(currentTest),
                });
            }

            flow = {
                SessionId: analysisSet.session.id,
                Tests: tests,
            };

            setAnalysisSet(analysisSet);

            setSession(analysisSet.session);

            await SetAnalysisSets(analysisSet.sessionId);
            ConstructMetadata(analysisSet.session, analysisSet);

            FetchSvcResultsData().then(async results => {
                await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${analysisSet.session.visit.subject.site.studyId}`, undefined, "fixedCommentSets")
                    .then(studySets => SetTechnicianCommentAvailability(studySets.value));

                SetSvcResults(results);
                setVolumeData(flow);
            });

            if (analysisSet.status === OverreadStatusIdEnum.Rejected)
                setIsAnalysisRejected(true);
        }
        
        initializeData();

    }, [analysisSetId, viewState]);

    const SetTechnicianCommentAvailability = (studySets: StudyFixedCommentSet[]) => {
        const technicianCommentSets = studySets.find(at => at.commentSetType === FixedCommentSetTypeEnum.Technician && at.sessionType === SessionTypes.Svc)?.fixedCommentSets;

        if (!technicianCommentSets) {
            return;
        }

        setTechnicianSessionCommentsAvailable(technicianCommentSets.find(scs => scs.entityType === FixedCommentEntityTypeEnum.Session) !== null);
        setTechnicianTestCommentsAvailable(technicianCommentSets.find(scs => scs.entityType === FixedCommentEntityTypeEnum.Test) !== null);
    };

    const [graphState, graphDispatch] = useSvcGraphs(volumeData);

    const fetchBestValues = async () => {
        acceptabilityDispatch(LoadingBestValuesAction.create())
        acceptabilityDispatch(UpdateBestTestAction.create(await legacyClient.calculateSvcBestTests(acceptabilityState.spirometryResults.TestResults)));
        acceptabilityDispatch(UpdateRepeatabilityAction.create(await legacyClient.calculateSvcRepeatability(acceptabilityState.spirometryResults.TestResults)));
        acceptabilityDispatch(DoneLoadingBestValuesAction.create());
    }

    React.useEffect(() => {
        if (acceptabilityState.spirometryResults.TestResults.length) {
            fetchBestValues();
        }
    }, [SvcResults, acceptabilityState.changedAcceptability, acceptabilityState.spirometryResults.AnalysisSetId])

    const graphData: SvcGraphTestData[] = React.useMemo(() => {
        if (!(graphState.flow && volumeData))
            return;

        return graphState.flow.Tests.map(vt => {
            const firstCycle = extractFirstCycle(vt.VolumeData);
            const expiratoryCycle = extractExpiratoryCurveData(vt.VolumeData, firstCycle);
            const tZeroAdjustedDataFull = adjustTimeToTZero(vt.VolumeData, expiratoryCycle);
            const expiratoryOnlyData = vt.VolumeData.slice(expiratoryCycle.startOfTestIndex, expiratoryCycle.endOfTestIndex);

            return {
                TestId: vt.TestId,
                TestNumber: vt.TestNumber,
                ColorHex: vt.ColorHex,
                IsDisplayed: vt.IsDisplayed,
                VolumeData: vt.VolumeData,
                ExpiratoryOnlyData: expiratoryOnlyData,
                TZeroAdjustedExpiratoryOnlyData: undefined,
                TZeroAdjustedData: tZeroAdjustedDataFull
            }
        })
    }, [graphState.flow, volumeData]);

    if (!(volumeData && graphState.flow && sessionMetadata && analysisSet && fixedCommentState.areAvailableFixedCommentsLoaded !== null && sessionNotes !== null)) {
        return (<LoadingWithMessage message="Loading SVC Data..." />);
    }

    const goBackToList = () => GoBackToList(history, currentUserPermissions, "/Svc", PermissionEnum.OverreadSvcSession, "/Svc/Analysis");
    const hasAddingNotePermission = currentUserPermissions.some(x=>x.Id === PermissionEnum.AddSvcSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.ViewSvcSessionNotes);
    
    const isQCAnalysisSet = sessionMetadata.analysisSet.type === SpirometryAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = sessionMetadata.analysisSet.type === SpirometryAnalysisSetTypeEnum.Site;
    const siteAnalysisSetId = analysisSets.find(x => x.type === SpirometryAnalysisSetTypeEnum.Site).id;

    const overreadingDisabled = viewState === "overread"
        && (fixedCommentState.areAvailableFixedCommentsLoaded === false || (fixedCommentState.areFixedCommentsLoaded === undefined || fixedCommentState.areFixedCommentsLoaded === null || fixedCommentState.areFixedCommentsLoaded === false));

    return (<div>

        <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
            Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
        </Alert>

        <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
            {error}
        </Alert>

        {!isQCAnalysisSet && !isSiteAnalysisSet && !isViewOnly && sessionMetadata.session.overreadChallengeStatus === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.session.challengeIsClosed ?
            <ChallengeAlert
                sessionId={sessionMetadata.session.id}
                sessionType={SessionTypes.Svc}
                onRejectComplete={goBackToList}
            /> : null}

        <h2 data-automationid="SvcAnalysisPageHeader" className="text-primary">Spirometry Data</h2>
        {!isQCAnalysisSet ?
            <SvcSessionMetadataTable
                sessionMetadata={sessionMetadata}/>
          
            : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}


        <h2 className="text-primary">Charts</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="btn-group pull-right">
                                <button className="btn btn-primary" onClick={() => setAdjustedToTZero(!adjustedToTZero)}>
                                    <i className={tZeroToggleClasses} aria-hidden="true"></i>&nbsp;TZero Adjust
                                </button>
                            </div>
                        </div>
                    </div>
                <SvcGraphs data={graphData} tZeroAdjust={adjustedToTZero} />
                </div>
        </div>

        <h2 className="text-primary" data-automationid="SvcAnalysisResultsPanel">Results</h2>
        <div className="panel panel-default">
            <div className="panel-body">
                <SvcResultsTable
                    isViewOnly={isViewOnly}
                    analysisSetId={analysisSetId}
                    graphState={graphState}
                    isRejected={isAnalysisRejected}
                    acceptabilityDispatch={acceptabilityDispatch}
                    acceptabilityState={acceptabilityState}
                    graphDispatch={graphDispatch}
                    isSiteAnalysisSet={isSiteAnalysisSet}
                    resultData={acceptabilityState.spirometryResults as SvcResults}
                    siteAnalysisSetId={siteAnalysisSetId}
                    fixedCommentsState={fixedCommentState}
                    fixedCommentsDispatch={fixedCommentDispatch}
                    technicianSessionCommentsAvailable={technicianSessionCommentsAvailable}
                    technicianTestCommentsAvailable={technicianTestCommentsAvailable}
                    studySvcRelevantParameters={studySvcRelevantParameters}
                />
            </div>
        </div>
        {
            hasViewNotePermission ? 
            <SummaryPanel
            notesButtonName="Session Notes"
            showNotesCount={true}
            isLoading={acceptabilityState.loadingBestValues}
            isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission}
            notes={sessionNotes}
            onAddNote={(annotation) => addNewNote(annotation)}
            />
            : null
        }
        <AcceptabilityDashboard
            analysisSetId={analysisSetId}
            isViewOnly={isViewOnly || overreadingDisabled}
            fixedCommentsDispatch={fixedCommentDispatch}
            fixedCommentState={fixedCommentState}
            acceptabilityState={acceptabilityState}
            sessionType={SessionTypes.Svc}  
            overreadStatus={sessionMetadata.analysisSet.status}
            onNoSessionCommentsSelected={() => {
                fixedCommentDispatch(ToggleSessionFixedCommentsVisibilityAction.create());
            }}
            onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
            onCancel={goBackToList}
        />
       </div>);
}
